/**
 * fetchOwnerDisplay — single primitive for resolving "what should we show
 * for this owner GUID?" across project surfaces (project detail panel,
 * project hero, project cards, project rows).
 *
 * Per DEV-895 Phase 2 / DEV-965, project ownership can be:
 *   - a community (organization-owned project) — display the community's
 *     effective logo via the inheritance cascade
 *   - a workshop community (community with `personal_owner_user_guid`
 *     set per ADR-006) — display the owning USER's avatar, not the
 *     workshop's logo. Workshops are technically communities but
 *     functionally "the user's private home for projects."
 *   - a user GUID directly (legacy paths still pass user_guid as
 *     owner_guid)
 *
 * The helper normalises all three cases into a single shape so callers
 * don't have to special-case anything. Returns avatarUrl=null when
 * nothing is set anywhere in the chain — callers render the
 * `fallbackInitial` (first letter) as the last resort.
 */
import { fetchUserByGuid } from "./security";
import { resolveEffectiveLogoUrl, fetchCommunityEntityByGuid } from "./communityMetadata";
import { mediaSrc } from "./mediaUrl"; // tolerant image-src resolver for user avatars

export interface OwnerDisplay {
  /** What kind of entity this owner is. Useful for routing / link targets. */
  kind: "community" | "user" | "unknown";
  /** Display name (community.name or user.displayName). Empty when unresolved. */
  displayName: string;
  /** Resolved avatar URL with cascade applied. NULL when nothing's set. */
  avatarUrl: string | null;
  /** First letter of displayName for the empty-state fallback. */
  fallbackInitial: string;
}

const EMPTY: OwnerDisplay = {
  kind: "unknown",
  displayName: "",
  avatarUrl: null,
  fallbackInitial: "",
};

/**
 * Resolve owner display data for the given owner GUID.
 *
 * @param ownerGuid - The owner GUID (community OR user). Pass-through of
 *   either; the helper figures out which.
 * @param communityEntities - Seed-shape community rows (snake_case keys).
 *   Used to detect community-owned (vs user-owned) and to walk the
 *   logo-inheritance cascade for community owners.
 * @param relationships - Seed-shape community_entity_relationships rows
 *   (snake_case keys). Used by the cascade walker.
 */
export async function fetchOwnerDisplay(
  ownerGuid: string | null | undefined,
  communityEntities: Array<Record<string, any>> | null | undefined,
  relationships: Array<Record<string, any>> | null | undefined,
): Promise<OwnerDisplay> {
  if (!ownerGuid) return EMPTY;
  const normalised = String(ownerGuid).toUpperCase();

  let community = (communityEntities ?? []).find(
    (e) => String(e.entity_guid ?? (e as any).entityGuid ?? "").toUpperCase() === normalised,
  );

  // DEV-1032 — soft miss in the local array (typically because the
  // community is private and got filtered out of the caller's bulk-
  // hydration response). Do a per-GUID server lookup before falling
  // through to user. Without this branch, private community owners
  // trigger a user-by-guid 404 that the browser logs to console.
  if (!community) {
    const fetched = await fetchCommunityEntityByGuid(ownerGuid);
    if (fetched) {
      // Normalise to the snake_case shape the rest of this function uses.
      community = {
        entity_guid: (fetched as any).entityGuid ?? (fetched as any).entity_guid,
        name: (fetched as any).name,
        entity_type: (fetched as any).entityType ?? (fetched as any).entity_type,
        hero_image_url: (fetched as any).heroImageUrl ?? (fetched as any).hero_image_url,
        logo_url: (fetched as any).logoUrl ?? (fetched as any).logo_url,
        personal_owner_user_guid:
          (fetched as any).personalOwnerUserGuid ?? (fetched as any).personal_owner_user_guid,
        is_active: (fetched as any).isActive ?? (fetched as any).is_active,
        visibility: (fetched as any).visibility,
      };
    }
  }

  if (community) {
    // Workshop case — the community is a personal home for a user. Resolve
    // to the user's profile avatar instead of the workshop's logo. Per
    // ADR-006 / DEV-896, `personal_owner_user_guid` is the back-link.
    const personalOwner = String(
      community.personal_owner_user_guid ?? (community as any).personalOwnerUserGuid ?? "",
    ).trim();
    if (personalOwner) {
      try {
        const user = await fetchUserByGuid(personalOwner);
        const displayName = user.displayName || user.name || "";
        return {
          kind: "user",
          displayName,
          avatarUrl: mediaSrc(user.profileImageUrl),
          fallbackInitial: initialOf(displayName),
        };
      } catch {
        return { ...EMPTY, kind: "user" };
      }
    }

    // Regular community owner — effective logo via the inheritance cascade.
    const name = String(community.name ?? "");
    const avatar = resolveEffectiveLogoUrl(normalised, communityEntities ?? [], relationships ?? []);
    return {
      kind: "community",
      displayName: name,
      avatarUrl: avatar,
      fallbackInitial: initialOf(name),
    };
  }

  // Not a community → treat as a user GUID.
  try {
    const user = await fetchUserByGuid(ownerGuid);
    const displayName = user.displayName || user.name || "";
    return {
      kind: "user",
      displayName,
      avatarUrl: mediaSrc(user.profileImageUrl),
      fallbackInitial: initialOf(displayName),
    };
  } catch {
    return EMPTY;
  }
}

function initialOf(name: string): string {
  const trimmed = (name || "").trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase();
}
